import {
  confirm,
  createSortableColumn,
  cn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import {
  formatDateTimeLocal,
  formatTimeLocal,
} from "@/shared/lib/date-time-performance";
import { RowActions } from "@/shared/ui/RowActions";
import { intervalToDuration } from "date-fns";
import { EditIcon, EyeIcon, TrashIcon } from "lucide-react";
import type { EventDTO } from "../../domain/contracts";
import { EVENT_FEATURES_MANUAL_EVENT, EVENT_FEATURES_PERFORMANCE_SHOW } from "../../domain/services/event-features";
import { EventFeatureChips } from "./EventFeatureChips";
import { EventProgressBar } from "./EventProgressBar";

type EventRow = EventDTO;

const STATUS_LABELS: Record<string, string> = {
  draft: "ກຳລັງວາງແຜນ",
  confirmed: "ຍືນຍັນແລ້ວ",
  completed: "ສຳເລັດ",
  cancelled: "ຍົກເລີກ",
};

const STATUS_COLORS: Record<string, string> = {
  draft:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  confirmed:
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  completed: "bg-muted text-muted-foreground",
  cancelled: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const EVENT_TABLE_WARNING_ROW_CLASS = cn(
  "[&_[data-slot=table-row]:has([data-warning-row=true])_[data-slot=table-cell]]:bg-amber-50/95",
  "[&_[data-slot=table-row]:has([data-warning-row=true]:hover)_[data-slot=table-cell]]:bg-amber-100/95",
  "dark:[&_[data-slot=table-row]:has([data-warning-row=true])_[data-slot=table-cell]]:bg-amber-950/35",
  "dark:[&_[data-slot=table-row]:has([data-warning-row=true]:hover)_[data-slot=table-cell]]:bg-amber-950/50",
);

type EventTableProps = {
  data: EventRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (row: EventRow) => void;
  onEdit: (row: EventRow) => void;
  onDelete: (id: string) => void;
};

function fmtDate(d: Date | string | null | undefined): string {
  if (!d) return "—";
  return formatDateTimeLocal(new Date(d));
}

function fmtEndDuration(
  endDate: Date | string | null | undefined,
  startDate: Date | string | null | undefined,
): string {
  if (!endDate) return "—";
  const end = new Date(endDate);
  const endStr = formatTimeLocal(end.toISOString());
  if (!startDate) return endStr;
  const start = new Date(startDate);
  const dur = intervalToDuration({ start, end });
  const parts: string[] = [];
  if (dur.hours) parts.push(`${dur.hours}h`);
  if (dur.minutes) parts.push(`${dur.minutes}m`);
  const durStr = parts.length ? ` (${parts.join(" ")})` : "";
  return `${endStr}${durStr}`;
}

export function EventTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onView,
  onEdit,
  onDelete,
}: EventTableProps) {
  const columns: TanstackReactTable.ColumnDef<EventRow>[] = [
    {
      ...createSortableColumn("title", "ຊື່ງານ"),
      cell: ({ row }) => {
        const { title, id, wasPostponed, postponeCount, dateTbd, teamAssignmentWarning } =
          row.original;
        return (
          <div data-warning-row={teamAssignmentWarning ? "true" : undefined}>
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="font-semibold text-sm leading-snug">{title}</p>
              {teamAssignmentWarning ? (
                <span
                  className="inline-flex items-center rounded bg-amber-500 px-1.5 py-0.5 font-semibold text-[10px] text-white tracking-wide"
                  title="ເຫຼືອ ≤9 ວັນ ແຕ່ຍັງບໍ່ຈັດທີມ"
                >
                  ຍັງບໍ່ຈັດທີມ
                </span>
              ) : null}
              {wasPostponed || dateTbd ? (
                <span
                  className="inline-flex items-center rounded bg-amber-500 px-1.5 py-0.5 font-semibold text-[10px] text-white tracking-wide"
                  title={
                    dateTbd
                      ? "ເລື່ອນບໍ່ມີກຳນົດ"
                      : postponeCount && postponeCount > 1
                        ? `ເລື່ອນແລ້ວ ${postponeCount} ຄັ້ງ`
                        : "ເລື່ອນວັນ"
                  }
                >
                  {dateTbd ? "ຍັງບໍ່ກຳນົດ" : "ເລື່ອນ"}
                  {!dateTbd && postponeCount && postponeCount > 1
                    ? ` ×${postponeCount}`
                    : ""}
                </span>
              ) : null}
            </div>
            <p className="mt-0.5 text-muted-foreground text-xs">
              ID: {id.slice(0, 10)}
            </p>
          </div>
        );
      },
    },
    {
      id: "category",
      header: "ຫມວດ / Features",
      cell: ({ row }) => {
        const features =
          row.original.eventFeatures ??
          (row.original.billingMode === "manual"
            ? EVENT_FEATURES_MANUAL_EVENT
            : EVENT_FEATURES_PERFORMANCE_SHOW);
        return (
          <div className="min-w-40">
            <p className="font-medium text-sm leading-snug">
              {row.original.categoryName ?? "—"}
            </p>
            <EventFeatureChips features={features} max={2} />
          </div>
        );
      },
    },
    {
      id: "eventDate",
      header: "ວັນເວລາເລີ່ມ",
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {row.original.dateTbd
            ? "ຍັງບໍ່ກຳນົດ"
            : fmtDate(row.original.eventDate)}
        </span>
      ),
    },
    {
      id: "endDuration",
      header: "ສິ້ນສຸດ/ຊ່ວງເວລາ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm tabular-nums">
          {row.original.dateTbd
            ? "—"
            : fmtEndDuration(row.original.endDate, row.original.eventDate)}
        </span>
      ),
    },
    {
      id: "locationText",
      header: "ສະຖານທີ່",
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.locationText ?? (
            <span className="text-muted-foreground">—</span>
          )}
        </span>
      ),
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const s = row.original.status;
        return (
          <span
            className={`inline-flex items-center rounded px-2 py-0.5 font-semibold text-xs tracking-wide ${STATUS_COLORS[s] ?? STATUS_COLORS.draft}`}
          >
            {STATUS_LABELS[s] ?? s}
          </span>
        );
      },
    },
    {
      id: "progress",
      header: "ຄວາມຄືບໜ້າ",
      cell: ({ row }) => (
        <EventProgressBar progress={row.original.progress} />
      ),
    },
    {
      id: "actions",
      header: "ຈັດການ",
      cell: ({ row }: { row: { original: EventRow } }) => {
        const { id } = row.original;
        return (
          <RowActions
            actions={[
              {
                label: "ເບິ່ງລາຍລະອຽດ",
                icon: <EyeIcon className="h-4 w-4" />,
                onClick: () => onView(row.original),
              },
              {
                label: "ແກ້ໄຂ",
                icon: <EditIcon className="h-4 w-4" />,
                onClick: () => onEdit(row.original),
              },
              {
                label: "ລຶບ",
                variant: "destructive" as const,
                icon: <TrashIcon className="h-4 w-4" />,
                onClick: async () => {
                  const ok = await confirm({
                    title: "ລຶບງານສະແດງ",
                    description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບງານສະແດງນີ້?",
                    actionText: "ລຶບ",
                    ActionProps: { variant: "destructive" },
                  });
                  if (ok) onDelete(id);
                },
              },
            ]}
            maxInline={2}
          />
        );
      },
    },
  ];

  return (
    <div className={EVENT_TABLE_WARNING_ROW_CLASS}>
      <DataTable<EventRow, unknown>
        noDataMessage="ບໍ່ພົບງານສະແດງ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
      />
    </div>
  );
}
